import { pushedByLabel } from '@/lib/format'
import { isSafeRepoUrl } from '@/lib/repoLinks'
import { ActorIcon, RepoProviderIcon } from '@/shared/components/RepoProviderIcon'
import { Tooltip } from '@/shared/components/Tooltip'

/**
 * The single, merged "Pushed by" cell for the state versions table - it replaces
 * the former separate "Source" + "Pushed by" columns. The cell value is an
 * icon-only trigger with a hover/focus popout that combines WHO pushed (actor type
 * + identity) with the VCS source provenance when the push came from CI.
 *
 * Icon family: GitHub/GitLab logos for VCS OIDC pushes (the logo IS the link to the
 * exact commit, exactly as the old SourceRepoLink behaved); a person/chip/question
 * glyph for human/service-account/legacy rows. All inline-SVG, currentColor.
 *
 * The trigger is a focusable <a>/<button> (never a bare <span>) so the Tooltip can
 * clone its ref + focus handlers and keyboard/screen-reader users can reach it.
 * Click/keydown propagation is stopped so activating the icon never triggers the
 * surrounding clickable row. All of this is DISPLAY-ONLY - never an authz signal.
 */

export interface PushedByCellProps {
  actorType: string | null
  /** email (human) / client id (bot) / OIDC sub (vcs); null for legacy rows. */
  actorName: string | null
  vcsProvider: string | null
  vcsRepository: string | null
  // NOTE: vcsRef, NOT `ref` - `ref` is a reserved React prop.
  vcsRef: string | null
  vcsRefType: string | null
  vcsSha: string | null
  vcsCommitUrl: string | null
}

export function PushedByCell({
  actorType,
  actorName,
  vcsProvider,
  vcsRepository,
  vcsRef,
  vcsRefType,
  vcsSha,
  vcsCommitUrl,
}: PushedByCellProps) {
  const { kind, label } = pushedByLabel(actorType, vcsProvider)
  const isVcs = kind === 'github' || kind === 'gitlab'

  // Popout rows - only the ones with data. The ref row's LABEL conveys branch/tag,
  // so the VALUE is just the ref name.
  const rows: Array<[string, string]> = []
  if (isVcs) {
    if (vcsRepository) rows.push(['Repository', vcsRepository])
    if (vcsRef) rows.push([vcsRefType === 'tag' ? 'Tag' : vcsRefType === 'branch' ? 'Branch' : 'Ref', vcsRef])
    if (vcsSha) rows.push(['Commit', vcsSha])
    if (actorName) rows.push(['Identity', actorName])
  } else if (actorName) {
    rows.push(['Identity', actorName])
  }

  const tip = (
    <span style={{ display: 'grid', gap: 6, textAlign: 'left', minWidth: 160 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text2)' }}>
        {isVcs ? <RepoProviderIcon provider={vcsProvider} size={14} /> : <ActorIcon kind={kind} size={14} />}
        <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      </span>
      {rows.length > 0 && (
        <span style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, rowGap: 3, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
          {rows.map(([k, v]) => (
            <span key={k} style={{ display: 'contents' }}>
              <span style={{ color: 'var(--text3)', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{k}</span>
              {/* nowrap so the full commit hash / email stays on one line; the box
                  auto-sizes (Tooltip maxWidth="none") instead of clipping. */}
              <span style={{ color: 'var(--text)', fontSize: '0.72rem', fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{v}</span>
            </span>
          ))}
        </span>
      )}
    </span>
  )

  const linkable = isVcs && isSafeRepoUrl(vcsCommitUrl)
  const aria = isVcs
    ? `pushed by ${label}: ${vcsRepository ?? ''}${vcsSha ? `@${vcsSha.slice(0, 8)}` : ''}`
    : `pushed by ${label}${actorName ? `: ${actorName}` : ''}`

  // Bare icon-button chrome: no background/border/padding, inherits colour, focusable.
  const triggerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: linkable ? 'pointer' : 'help',
    color: linkable ? 'var(--accent)' : 'var(--text2)',
  } as const

  const icon = isVcs
    ? <RepoProviderIcon provider={vcsProvider} size={18} />
    : <ActorIcon kind={kind} size={18} />

  const trigger = linkable ? (
    <a
      href={vcsCommitUrl as string}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={aria}
      className="icon-trigger"
      style={{ ...triggerStyle, textDecoration: 'none' }}
      onClick={(e) => e.stopPropagation()}
    >
      {icon}
    </a>
  ) : (
    <button
      type="button"
      aria-label={aria}
      className="icon-trigger"
      style={triggerStyle}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
    >
      {icon}
    </button>
  )

  // maxWidth="none" lets the box auto-size to its content so the full commit hash /
  // email isn't clipped by the global 300px tooltip cap.
  // interactive so the user can move onto the box and select/copy the sha / email.
  return <Tooltip text={tip} placement="top" maxWidth="none" interactive>{trigger}</Tooltip>
}
